import React, { useEffect, useState } from 'react';
import { gql } from '@apollo/client';
import { useQuery, useMutation } from 'hooks/apollo';

import { Form, Table, Alert } from 'antd';
import SurveyTitle from 'components/SurveyTitle';
import './edit-question.less';
import { FormattedMessage } from 'react-intl';
import { renderTranslation } from '../../TranslateSurvey';
import messages from './messages';

const QUERY = gql`
  query EditQuestionGroup($id: ID!) {
    survey(id: $id) {
      id
      languageParent {
        id
        children {
          id
          title
          language
          languageName
          questions {
            id
            title
            settings { type }
            group { id }
            options {
              id
              title
              group { id }
            }
          }
        }
      }
    }
  }
`;

const UPDATE = gql`
  mutation ($input: UpdateSurveyTranslationsInput!) {
    updateSurveyTranslations(input: $input)
  }
`;

const getInitialValues = surveys => {
  const questions = {};
  const options = {};

  surveys.forEach(survey => {
    survey.questions.forEach(q => {
      questions[q.id] = q.title;
      q.options.forEach(o => {
        options[o.id] = o.title;
      });
    });
  });

  return {
    questions,
    options,
  };
};

const getTranslationColumn = ({ survey, onDisconnect, form, disconnected, index }) => {
  const { questions } = survey;
  const translations = questions.reduce((acc, q) => {
    if (q.group) {
      acc[q.group.id] = q;
      q.options.forEach(o => {
        if (o.group) {
          acc[o.group.id] = o;
        }
      });
    }
    return acc;
  }, {});

  return {
    title: <SurveyTitle survey={survey} />,
    dataIndex: 'group',
    width: 400,
    render: (group, record) => renderTranslation({ group, record, translations, questions, index, form, onDisconnect, disconnected }),
  };
};

const initialDisconnects = { questions: [], options: [] };

const EditQuestionGroup = ({
  surveyId,
  question,
  onSubmit,
  form,
}) => {
  const { data, loading, refetch } = useQuery(QUERY, { variables: { id: surveyId } });
  const [updateTranslations] = useMutation(UPDATE);
  const [disconnected, setDisconnected] = useState(initialDisconnects);

  const surveys = data?.survey.languageParent?.children || [];

  const sourceSurvey = surveys.find(s => s.id === surveyId);
  const otherSurveys = surveys.filter(s => s.id !== surveyId);

  useEffect(() => form.resetFields(), [data]);

  if (!loading && otherSurveys.length === 0) {
    return <FormattedMessage {...messages.surveyNotInGroup} />;
  }

  const initialValues = { update: getInitialValues(otherSurveys) };

  const onDisconnect = (prop, id) => {
    setDisconnected({
      ...disconnected,
      [prop]: [
        ...disconnected[prop],
        id,
      ],
    });
  };

  const onFinish = async () => {
    const values = form.getFieldsValue(null, ({ touched }) => touched);

    await updateTranslations({ variables: {
      input: {
        surveyId,
        ...values,
        disconnect: disconnected,
      },
    } });

    await refetch();
    setDisconnected(initialDisconnects);

    onSubmit();
  };

  const columns = [
    {
      title: <SurveyTitle survey={sourceSurvey} />,
      dataIndex: 'title',
      width: 400,
      fixed: 'left',
    },
    ...otherSurveys.map((survey, index) => getTranslationColumn({ survey, onDisconnect, form, disconnected, index })),
  ];

  const dataSource = [{
    ...question,
    children: question.options.map(o => ({ ...o, question })),
  }];

  return (
    <Form form={form} onFinish={onFinish} initialValues={initialValues}>
      <Alert message={<FormattedMessage {...messages.questionGroupInfo} />} type="info" showIcon style={{ marginBottom: '1em' }} />
      <Table
        columns={columns}
        dataSource={dataSource}
        pagination={false}
        bordered
        expandable={{
          defaultExpandAllRows: true,
          expandIcon: () => null,
        }}
        scroll={{ x: 1000, y: 'calc(100vh - 350px)' }}
        size="small"
        loading={loading}
      />
    </Form>
  );
};

export default EditQuestionGroup;
